import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LayoutChangeEvent, StyleSheet, useWindowDimensions } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import Animated, { runOnUI, scrollTo, useAnimatedRef, useSharedValue } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useNavigation } from '@react-navigation/native';

import { EntryInfoFragment } from '@/api/entries/query.generated';
import { EventInfo } from '@/api/events/types/types';
import { ClearIcon } from '@/assets/icons/clearIcon';
import { BetSlipBalanceButton } from '@/components/BetSlipBalanceButton';
import { LeftSideTabBar, TABS_HEIGHT } from '@/components/LeftSideTabBar';
import { SheetScreen } from '@/components/ScreenComponent';
import { SCREEN_NAV_BAR_HEIGHT, SHEET_NAV_BAR_TOP_SPACING } from '@/components/ScreenNavBar';
import { SizedBox } from '@/components/SizedBox';
import { SubmissionOverlay } from '@/components/checkout/SubmissionOverlay';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { LinearGradient } from '@/components/linear-gradient/LinearGradient';
import {
    PROMO_WIDGET_HEIGHT,
    PROMO_WIDGET_OVERLAP,
    PROMO_WIDGET_VISIBLE_HEIGHT,
    PromoWidgetRef,
} from '@/components/promo-widget/PromoWidget';
import { BetrBucksWidget } from '@/feature/betslip-pickem/components/BetrBucksWidget';
import { PayoutBreakdown } from '@/feature/betslip-pickem/components/PayoutBreakdown';
import { PickemPromoWidget } from '@/feature/betslip-pickem/components/PickemPromoWidget';
import { useFantasyEntryPromotions } from '@/feature/betslip-pickem/hooks/use-fantasy-entry-promotions';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { CreateEntryTimeout } from '@/hooks/use-entries';
import { useKeyboardHeight } from '@/hooks/use-keyboard-height';
import { usePreventAndroidBackNavigation } from '@/hooks/use-prevent-android-back-navigation';
import { useResumeEffect } from '@/hooks/use-resume';
import { useWalletStore } from '@/hooks/use-wallet';
import { FantasyPickSlipNavigationProp } from '@/navigation/pickem/types';
import { common, designSystem, withOpacity } from '@/styles/styles';
import { GameMode } from '@/types/api.generated';
import { isWeb, showScrollIndicator } from '@/utils/constants-platform-specific';
import { logger } from '@/utils/logging';

import { CheckoutHeader } from '../components/CheckoutHeader';
import { EditQuickAmountsButton } from '../components/EditQuickAmountsButton';
import { EmptyPickSlipInfo } from '../components/EmptyPickSlipInfo';
import { Footer } from '../components/Footer';
import { Lineup } from '../components/Lineup';
import { AmountInputWidget } from '../components/amount-input/AmountInputWidget';
import { useBetslipActions } from '../hooks/use-betslip-actions';
import { useBetslipData } from '../hooks/use-betslip-data';
import { useBetslipPlayersFetch } from '../hooks/use-betslip-players-fetch';
import { useBetslipStore } from '../hooks/use-betslip-store';
import { useEntryMultiplier } from '../hooks/use-entry-multiplier';
import { useEntryRules } from '../hooks/use-entry-rules';
import { usePlaceEntry } from '../hooks/use-place-entry';
import { usePlaceEntryHandlers } from '../hooks/use-place-entry-handlers';
import { isFreeEntryPromo } from '../utils/betslip-utils';
import { getPickslipTabs } from '../utils/game-mode-utils';

export const FantasyPickSlip = () => {
    const { gameModesErrors, betslip: pickslip } = useBetslipData();

    // Dynamic mode should always be first selected, even if the entry is not eligible for this game mode
    // When only one game mode is returned by the BE, display the one that exists
    const activeGameModeTab = useBetslipStore(state => state.activeGameMode);

    const initAndGetEntryAmount = useBetslipStore(state => state.actions.initAndGetEntryAmount);
    const setEntryAmount = useBetslipStore(state => state.actions.setEntryAmount);
    const removeAllPicks = useBetslipStore(state => state.actions.clearBetslip);

    const entryAmount = useBetslipStore(state => state.entryAmount ?? 0);

    const currency = useBetslipStore(state => state.currency);
    const betslip = useMemo(() => pickslip, [pickslip]);
    const emptyBetslip = betslip.length === 0;
    const acceptedEdgeCombosCount = useBetslipStore(store => store.acceptedEdgeCombos.length);
    const { validatePicks, removeSelection, syncPicks } = useBetslipActions();
    const isListEmpty = betslip.length === 0;
    const headerRightItem = useMemo(() => <TrashIconWrapper />, []);

    useEffect(() => {
        return () => {
            // if the user leaves the pickslip with 0 amount, make it undefined so we can set the default next time he comes back
            const currentEntryAmount = useBetslipStore.getState().entryAmount;
            if (currentEntryAmount === 0) {
                setEntryAmount(undefined);
            }
        };
    }, [setEntryAmount]);

    const validatePicksOnResume = useCallback(() => {
        logger.debug('[PickSlip]', 'onResume() -> validate picks');
        // in order to avoid validating with the wrong amount, we set the default here
        // if the default is not set, it returns the entry amount from the store
        const validationAmount = initAndGetEntryAmount() ?? 0;

        // effect is fired when betslip or entryAmount changes
        // use 'cancelPrevious' so we cancel previous requests, and start a new one with the latest data
        validatePicks(betslip, validationAmount, currency, undefined, removeSelection, 'cancelPrevious');

        // to allow entryAmount and acceptedEdgeCombosCount as deps
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        betslip,
        currency,
        validatePicks,
        // used as an array dependency, because each time the entry amount changes we need to re-validate the picks
        entryAmount,
        // used as an array dependency, because each time the user accepts a new edge combo we need to re-validate the picks =>
        // this will result in the next edge combo dialog being shown, if for instance multiple edge-combos
        // entries are imported, or lineups reused
        acceptedEdgeCombosCount,
    ]);

    useResumeEffect(validatePicksOnResume);

    const validatingBetslip = useBetslipStore(state => state.validating);
    const validationData = useBetslipStore(state => state.validationData[activeGameModeTab]);
    const checkForBalanceError = useBetslipStore(state => state.actions.checkForBalanceError);

    // Balance shortfall is a reliable, always-current client-side check, so it takes priority.
    // An insufficient balance also trips the backend amount-validation error, so we must resolve
    // this first and exclude it from hasValidationError, otherwise the Deposit button never shows.
    const hasBalanceError = checkForBalanceError() && !validatingBetslip;
    const selectedPromo = useBetslipStore(state => state.selectedPromo);
    // a free entry is paid by the promo, so balance-related amount errors don't apply
    const freeEntryApplied = isFreeEntryPromo(selectedPromo);
    const amountErrorMessage = freeEntryApplied ? undefined : validationData.amountValidation?.errorMessage;

    const hasValidationError =
        !hasBalanceError &&
        entryAmount > 0 &&
        (!!amountErrorMessage || !!gameModesErrors[activeGameModeTab].errorMessage) &&
        !validatingBetslip &&
        entryAmount === validationData.betValidation?.amount;
    const errorMessage = amountErrorMessage ?? gameModesErrors[activeGameModeTab].errorMessage;

    const { validPicks } = useBetslipData();
    const { minNumberOfPicksMap } = useEntryRules();

    const hideFooter =
        emptyBetslip || !validPicks || betslip.length < minNumberOfPicksMap[activeGameModeTab] || entryAmount === 0;

    const navigation = useNavigation<FantasyPickSlipNavigationProp>();
    const amount = Number(entryAmount);
    const isMysteryMode = selectedPromo?.promotion.type === GameMode.Mystery;

    const onInvalidProjectionsFound = useCallback(() => {
        // In mystery mode the user has already navigated to the wheel/confirmation screen by the time
        // syncPicks runs, so dismissing the modal must go back to the pickslip. In other modes the
        // sync happens before navigation, so the user is still on the pickslip and no goBack is needed.
        syncPicks({
            expectError: true,
            promptIfOddsChanged: true,
            infoModalDismissBehaviour: isMysteryMode ? 'goBack' : 'none',
        });
    }, [syncPicks, isMysteryMode]);

    const multiplier = useEntryMultiplier();

    const goToSubmitEntry = useCallback(
        (entry?: EntryInfoFragment, isTimeout?: boolean) => {
            navigation.push('SubmitEntry', {
                amount,
                picks: betslip,
                entry,
                onInvalidProjectionsFound,
                isTimeout,
                isMysteryMode,
                isFreeEntry: freeEntryApplied,
                multiplier,
            });
        },
        [amount, betslip, navigation, onInvalidProjectionsFound, isMysteryMode, freeEntryApplied, multiplier]
    );

    const { handleSuccess, handleError } = usePlaceEntryHandlers({ onInvalidProjectionsFound });

    const onSuccess = useCallback(
        async (entry: EntryInfoFragment) => {
            await handleSuccess(entry);
            goToSubmitEntry(entry);
        },
        [handleSuccess, goToSubmitEntry]
    );

    const onError = useCallback(
        (error: unknown) => {
            if (error instanceof CreateEntryTimeout) {
                goToSubmitEntry(undefined, true);
                return;
            }
            handleError(error);
        },
        [handleError, goToSubmitEntry]
    );

    const { placeEntry, isLoading } = usePlaceEntry({
        picks: betslip,
        onSuccess,
        onError,
    });

    usePreventAndroidBackNavigation(isLoading);

    return (
        <>
            <SheetScreen style={common.flex} edges={['top']}>
                <MaxWidthWrapper>
                    <CheckoutHeader
                        onRightItemPress={removeAllPicks}
                        rightItem={isListEmpty ? null : headerRightItem}
                        balanceButton={<BetSlipBalanceButton testId="pickSlipBalanceButton" />}
                    />
                </MaxWidthWrapper>

                <PickSlipContent hideFooter={hideFooter} />
                {isLoading ? <SubmissionOverlay /> : null}
            </SheetScreen>
            {hideFooter ? null : (
                <Footer
                    errorMessage={errorMessage}
                    hasBalanceError={hasBalanceError}
                    hasValidationError={hasValidationError}
                    placeEntry={placeEntry}
                    isLoading={isLoading}
                    goToSubmitEntry={goToSubmitEntry}
                />
            )}
        </>
    );
};

const LineupWithErrorInfo = ({
    betslipLength,
    events,
    loading,
}: {
    betslipLength: number;
    events?: EventInfo[];
    loading: boolean;
}) => {
    const insets = useSafeAreaInsets();
    return (
        <ScrollView
            contentContainerStyle={[styles.lineupErrorScroll, { paddingBottom: insets.bottom }]}
            showsVerticalScrollIndicator={showScrollIndicator}
        >
            <MaxWidthWrapper>
                <Box paddingHorizontal={'s16'} gap={'s16'} paddingTop={'s24'}>
                    <Lineup events={events} loading={loading} />
                </Box>
                <Box flex={1} paddingVertical={isWeb ? 's24' : undefined}>
                    <EmptyPickSlipInfo betslipLength={betslipLength} />
                </Box>
            </MaxWidthWrapper>
        </ScrollView>
    );
};

const PickSlipContent = ({ hideFooter }: { hideFooter: boolean }) => {
    const betslip = useBetslipStore(state => state.betslip);

    const { validPicks } = useBetslipData();
    const { minNumberOfPicks } = useEntryRules();
    const notEnoughPlayersInBetsplip = betslip.length < minNumberOfPicks;
    const emptyBetslip = betslip.length === 0;
    const { events, loading } = useBetslipPlayersFetch();

    // Empty betslip case
    if (emptyBetslip) {
        return <EmptyPickSlipInfo betslipLength={betslip.length} />;
    }

    // Not enough players or invalid picks case
    if (notEnoughPlayersInBetsplip || !validPicks) {
        return <LineupWithErrorInfo betslipLength={betslip.length} events={events} loading={loading} />;
    }

    // Game mode tabs and content
    return <PickSlipWithValidGameMode events={events} loading={loading} hideFooter={hideFooter} />;
};

const PickSlipWithValidGameMode = ({
    events,
    loading,
    hideFooter,
}: {
    events?: EventInfo[];
    loading: boolean;
    hideFooter: boolean;
}) => {
    const { pickslipEntryRules, betslip } = useBetslipData();
    const tabs = useMemo(() => getPickslipTabs(pickslipEntryRules), [pickslipEntryRules]);

    const activeGameModeTab = useBetslipStore(state => state.activeGameMode);
    const { minNumberOfPicksMap } = useEntryRules();
    const setActiveGameModeTab = useBetslipStore(state => state.actions.setActiveGameMode);
    const { validatePicks, removeSelection } = useBetslipActions();

    const { width } = useWindowDimensions();
    const initialTabLayout = useMemo(() => ({ width }), [width]);

    const onPressGameModeTab = useCallback(
        (selectedTabId: string) => {
            setActiveGameModeTab(selectedTabId as GameMode);

            const { entryAmount, currency } = useBetslipStore.getState();

            validatePicks(betslip, entryAmount ?? 0, currency, undefined, removeSelection, 'cancelPrevious');
        },
        [betslip, removeSelection, setActiveGameModeTab, validatePicks]
    );

    useEffect(() => {
        // if the active game mode tab is not in the tabs, switch to the other game mode
        if (!tabs.some(tab => tab.id === activeGameModeTab)) {
            setActiveGameModeTab(activeGameModeTab === GameMode.Dynamic ? GameMode.Perfect : GameMode.Dynamic);
            return;
        }
        // the dependency array is intentionally empty to run this check only once when the component mounts
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const initialIndex = tabs.findIndex(tab => tab.id === activeGameModeTab);
    const onlyOneTab = tabs.length === 1;

    return (
        <>
            {!onlyOneTab ? (
                <Box
                    height={TABS_HEIGHT}
                    alignItems={isWeb ? 'center' : undefined}
                    borderBottomWidth={1}
                    borderBottomColor={ds.colorToken.border.strong}
                    marginTop={'s10'}
                >
                    {
                        // we cannot render tabs if the initial index is -1
                        // which means the active game mode is not in the tabs
                        initialIndex !== -1 ? (
                            <LeftSideTabBar
                                location={'pickslip'}
                                scrollEnabled={false}
                                tabStyle={styles.tabStyles}
                                tabBarStyle={styles.tabBarStyle}
                                indicatorStyle={styles.indicatorStyle}
                                tabBarLabelStyle={styles.tabBarLabelStyle}
                                tabBarLabelFocusedColor={ds.colorToken.text.primary}
                                initialLayout={initialTabLayout}
                                // Setting the initial index to the active game mode so that the active tab and active game mode are in sync
                                // Otherwise we would have to add an effect to set the initial tab to be the active game mode
                                initialIndex={initialIndex}
                                gap={0}
                                tabs={tabs}
                                setSelectedTab={onPressGameModeTab}
                            />
                        ) : null
                    }
                </Box>
            ) : null}

            {betslip.length < minNumberOfPicksMap[activeGameModeTab] ? (
                <LineupWithErrorInfo betslipLength={betslip.length} events={events} loading={loading} />
            ) : (
                <PickSlipScrollableContent events={events} loading={loading} hideFooter={hideFooter} />
            )}
        </>
    );
};

// used Reanimated's createAnimatedComponent to avoid gesture/layout bugs on Android
// There are several issues with Animated.ScrollView, so don't use it directly
const AnimatedScrollView = Animated.createAnimatedComponent(ScrollView);

export const PickSlipScrollableContent = ({
    events,
    loading,
    hideFooter,
}: {
    events?: EventInfo[];
    loading: boolean;
    hideFooter: boolean;
}) => {
    const activeGameModeTab = useBetslipStore(state => state.activeGameMode);
    const betrBucks = useWalletStore(state => state.betrBucks);
    const promosWidgetRef = useRef<PromoWidgetRef>(null);

    const keyboardHeight = useKeyboardHeight();
    const { height } = useWindowDimensions();
    const { entryPromotions } = useFantasyEntryPromotions();

    const scrollViewRef = useAnimatedRef<Animated.ScrollView>();

    const insets = useSafeAreaInsets();

    const [isInputFocused, setIsInputFocused] = useState(false);

    const inputY = useSharedValue(0);
    const inputHeight = useSharedValue(0);
    const showEntryPromotionsWidget = entryPromotions.length > 0;

    const scrollToInput = useCallback(() => {
        runOnUI((y: number, h: number, windowHeight: number, keyboard: number, header: number, topInset: number) => {
            'worklet';
            if (h === 0) {
                // layout not available yet
                return;
            }

            const visibleH = windowHeight - topInset - header - keyboard;
            const target = y + h / 2 - visibleH / 2;

            scrollTo(scrollViewRef, 0, Math.max(0, target), true);
        })(inputY.value, inputHeight.value, height, keyboardHeight, HEIGHT_ABOVE_INPUT, insets.top);
    }, [height, inputHeight.value, inputY.value, insets.top, keyboardHeight, scrollViewRef]);

    const onInputWidgetLayout = useCallback(
        ({ nativeEvent }: LayoutChangeEvent) => {
            inputY.value = nativeEvent.layout.y;
            inputHeight.value = nativeEvent.layout.height;
        },
        [inputHeight, inputY]
    );

    const onFocusInput = useCallback(() => {
        setIsInputFocused(true);
    }, []);

    const onBlurInput = useCallback(() => {
        setIsInputFocused(false);
    }, []);

    useEffect(() => {
        if (isInputFocused && keyboardHeight > 0) {
            if (promosWidgetRef.current?.arePromosExpanded()) {
                promosWidgetRef.current.collapsePromos(() => {
                    scrollToInput();
                });
            } else {
                scrollToInput();
            }
        }
    }, [isInputFocused, keyboardHeight, scrollToInput]);

    return (
        <AnimatedScrollView
            ref={scrollViewRef}
            showsVerticalScrollIndicator={showScrollIndicator}
            keyboardDismissMode="on-drag"
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={hideFooter ? { paddingBottom: insets.bottom } : undefined}
        >
            <MaxWidthWrapper>
                <Box pb={'s48'} paddingHorizontal={'s16'} gap={'s16'} style={styles.container}>
                    {betrBucks > 0 ? <BetrBucksWidget /> : null}
                    <Box onLayout={onInputWidgetLayout}>
                        {showEntryPromotionsWidget ? (
                            <PickemPromoWidget ref={promosWidgetRef} />
                        ) : (
                            <SizedBox value={PROMO_WIDGET_HEIGHT - PROMO_WIDGET_VISIBLE_HEIGHT} />
                        )}
                        <Box style={styles.amountInputWidgetWithPromotions}>
                            <AmountInputWidget onFocusInput={onFocusInput} onBlurInput={onBlurInput} />
                            <LinearGradient
                                colors={[
                                    designSystem.colors.neutralBlack,
                                    withOpacity(designSystem.colors.neutralBlack, 0.5),
                                    withOpacity(designSystem.colors.neutralBlack, 0.3),
                                    withOpacity(designSystem.colors.neutralBlack, 0.08),
                                    designSystem.colors.transparent,
                                ]}
                                start={{ x: 0, y: 1 }}
                                end={{ x: 0, y: 0 }}
                                style={styles.topGradient}
                            />
                        </Box>
                    </Box>
                    {activeGameModeTab ? <PayoutBreakdown /> : null}
                    <Lineup events={events} loading={loading} />
                    <EditQuickAmountsButton />
                </Box>
            </MaxWidthWrapper>
        </AnimatedScrollView>
    );
};

const TrashIconWrapper = () => {
    return (
        <Box padding={'s12'}>
            <ClearIcon color={ds.colorValue.background.danger.strong} />
        </Box>
    );
};

const HEADER_GAP = 8;

const styles = StyleSheet.create({
    container: {
        paddingTop: 23, // -1px because we have the border under the left side tab bar
    },
    tabStyles: {
        width: undefined,
    },
    tabBarStyle: {
        height: TABS_HEIGHT,
    },
    indicatorStyle: {
        marginBottom: 1,
    },
    tabBarLabelStyle: {
        bottom: 8,
    },
    headerContainer: {
        gap: HEADER_GAP,
    },
    tabsContainer: {
        flex: 0,
        marginTop: 8,
    },
    lineupErrorScroll: {
        flexShrink: 0,
        flexGrow: 1,
    },
    amountInputWidgetWithPromotions: {
        marginTop: PROMO_WIDGET_OVERLAP,
        position: 'relative',
    },
    topGradient: {
        position: 'absolute',
        top: -20,
        left: 0,
        right: 0,
        height: 60,
        pointerEvents: 'none',
        zIndex: -1,
    },
});

// the difference between the current screen (which is open as a modal) and the screen behind it
const SCREEN_MODAL_OFFSET = 10;

const HEIGHT_ABOVE_INPUT =
    SHEET_NAV_BAR_TOP_SPACING + SCREEN_NAV_BAR_HEIGHT + HEADER_GAP + TABS_HEIGHT + SCREEN_MODAL_OFFSET;
